/**
 * Reading the Minimum Price off the wire.
 * Pure TypeScript - no React imports.
 *
 * The minimum is a Prisma `Decimal` on the backend, which `openapi-typescript` cannot
 * express: the contract types it `object, nullable` and the wire carries the string
 * `"5000.00"`. So the generated type is no help, and every slice that reads a minimum has
 * to coerce it the same way — which is why the coercion lives here rather than four times
 * over.
 *
 * The rule that matters: an absent, null or unreadable minimum is *no minimum*, never the
 * `0` a plain `Number()` would produce. A zero minimum means the customer may pay nothing,
 * which is a thing a coach chooses, not a thing a missing field decides for them.
 */

/**
 * The Minimum Price as a response can carry it: the decimal string it usually is, a
 * number, or nothing.
 */
export type WireMinimumPrice = string | number | null | undefined;

/**
 * Reads a wire Minimum Price as the number the domain holds.
 *
 * @param value - the minimum exactly as the response carried it
 * @returns the minimum, or `undefined` when the response carried none
 */
export function readWireMinimumPrice(value: WireMinimumPrice): number | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === "string" && value.trim() === "") {
    return undefined;
  }

  if (typeof value !== "string" && typeof value !== "number") {
    return undefined;
  }

  const minimum = Number(value);

  return Number.isFinite(minimum) ? minimum : undefined;
}
